import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import config from '../../config';
import { useAppContext } from '../AppContext';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Tooltip,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Grid,
  Stack,
  Divider,
  Switch,
  FormControlLabel
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const TIPUS_ACTIU_OPTIONS = [
  { value: 'ACCION_FONDO', label: 'Acció / Fons d’Inversió (ACCION_FONDO)' },
  { value: 'CRIPTO', label: 'Criptomoneda (CRIPTO)' },
  { value: 'METAL', label: 'Metall Preciós (METAL)' },
  { value: 'ETF', label: 'ETF (Fons Cotitzat)' },
  { value: 'ACCIO', label: 'Acció (ACCIO)' },
  { value: 'FONS', label: 'Fons (FONS)' },
  { value: 'IMMOBLE', label: 'Immoble (IMMOBLE)' },
  { value: 'DIPOSIT', label: 'Dipòsit Bancari (DIPOSIT)' },
  { value: 'ALTRES', label: 'Altres (ALTRES)' }
];

const ORIGEN_PREU_OPTIONS = [
  { value: 'API_YAHOO', label: 'Yahoo Finance (API_YAHOO)' },
  { value: 'API_COINGECKO', label: 'CoinGecko (API_COINGECKO)' },
  { value: 'API_XTB', label: 'Broker XTB (API_XTB)' },
  { value: 'MANUAL', label: 'Preu Manual (MANUAL)' }
];

const getTipusChipColor = (tipus) => {
  switch (tipus) {
    case 'CRIPTO':
      return 'warning';
    case 'ACCION_FONDO':
    case 'ACCIO':
      return 'primary';
    case 'METAL':
    case 'METALL':
      return 'secondary';
    case 'ETF':
    case 'FONS':
      return 'info';
    default:
      return 'default';
  }
};

const extractErrorMessage = (err, fallback) => {
  if (err.response?.status === 403) {
    return "Accés denegat (403): Cal el rol ROLE_ADMIN. Fes Logout i Login amb Google per actualitzar el token.";
  }
  return (
    err.response?.data?.detail ||
    err.response?.data?.message ||
    (typeof err.response?.data === 'string' ? err.response.data : null) ||
    fallback
  );
};

const ActiusAdmin = ({ userapp, showNotification }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { ctxUser } = useAppContext();
  const jwtToken = ctxUser?.fdvToken?.jwtToken || '';

  const [actius, setActius] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [onlyActive, setOnlyActive] = useState(true);

  // Modal Crear / Editar
  const [openModal, setOpenModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    tickerIsin: '',
    tipusActiu: 'ACCION_FONDO',
    origenPreu: 'API_YAHOO',
    tickerPreu: '',
    tickerBroker: '',
    actiu: true
  });
  const [saving, setSaving] = useState(false);

  // Confirmació Desactivar Lògicament
  const [openToggleDialog, setOpenToggleDialog] = useState(false);
  const [toggleTarget, setToggleTarget] = useState(null);
  const [toggling, setToggling] = useState(false);

  // Carregar llista d'actius
  const fetchActius = useCallback(async () => {
    if (!jwtToken || !userapp) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${config.apiBaseUrl}/v1/inversions/actius/byuserapp/${userapp}?nomesActius=${onlyActive}`,
        { headers: { Authorization: jwtToken } }
      );
      const data = Array.isArray(res.data) ? res.data : [];
      setActius(data);
    } catch (err) {
      console.error('Error al carregar actius:', err);
      const msg = extractErrorMessage(err, 'Error al carregar el catàleg d’actius');
      showNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwtToken, userapp, onlyActive]);

  useEffect(() => {
    fetchActius();
  }, [fetchActius]);

  // Filtrar per cerca
  const filteredActius = useMemo(() => {
    return actius.filter((a) => {
      const q = searchTerm.toLowerCase();
      return (
        (a.nom && a.nom.toLowerCase().includes(q)) ||
        (a.tickerIsin && a.tickerIsin.toLowerCase().includes(q)) ||
        (a.tickerPreu && a.tickerPreu.toLowerCase().includes(q)) ||
        (a.tickerBroker && a.tickerBroker.toLowerCase().includes(q))
      );
    });
  }, [actius, searchTerm]);

  // Llistes dinàmiques que inclouen el valor actual si no és a la llista estàndard
  const availableTipusOptions = useMemo(() => {
    const current = formData.tipusActiu;
    if (current && !TIPUS_ACTIU_OPTIONS.some((o) => o.value === current)) {
      return [{ value: current, label: current }, ...TIPUS_ACTIU_OPTIONS];
    }
    return TIPUS_ACTIU_OPTIONS;
  }, [formData.tipusActiu]);

  const availableOrigenOptions = useMemo(() => {
    const current = formData.origenPreu;
    if (current && !ORIGEN_PREU_OPTIONS.some((o) => o.value === current)) {
      return [{ value: current, label: current }, ...ORIGEN_PREU_OPTIONS];
    }
    return ORIGEN_PREU_OPTIONS;
  }, [formData.origenPreu]);

  // Obrir Creació
  const handleOpenCreate = () => {
    setEditingItem(null);
    setFormData({
      nom: '',
      tickerIsin: '',
      tipusActiu: 'ACCION_FONDO',
      origenPreu: 'API_YAHOO',
      tickerPreu: '',
      tickerBroker: '',
      actiu: true
    });
    setOpenModal(true);
  };

  // Obrir Edició
  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setFormData({
      nom: item.nom || '',
      tickerIsin: item.tickerIsin || '',
      tipusActiu: item.tipusActiu || 'ACCION_FONDO',
      origenPreu: item.origenPreu || 'API_YAHOO',
      tickerPreu: item.tickerPreu || '',
      tickerBroker: item.tickerBroker || '',
      actiu: item.actiu ?? true
    });
    setOpenModal(true);
  };

  // Desar Actiu
  const handleSave = async () => {
    if (!formData.nom.trim()) {
      showNotification('El nom de l’actiu és obligatori', 'warning');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        userApp: userapp,
        nom: formData.nom.trim(),
        tickerIsin: formData.tickerIsin.trim(),
        tipusActiu: formData.tipusActiu,
        origenPreu: formData.origenPreu,
        tickerPreu: formData.tickerPreu.trim(),
        tickerBroker: formData.tickerBroker.trim(),
        actiu: formData.actiu
      };

      if (editingItem) {
        payload.id = editingItem.id;
        await axios.put(`${config.apiBaseUrl}/v1/inversions/actius/${editingItem.id}`, payload, {
          headers: { Authorization: jwtToken }
        });
        showNotification('Actiu d’inversió actualitzat correctament', 'success');
      } else {
        await axios.post(`${config.apiBaseUrl}/v1/inversions/actius/`, payload, {
          headers: { Authorization: jwtToken }
        });
        showNotification('Nou actiu creat correctament', 'success');
      }
      setOpenModal(false);
      fetchActius();
    } catch (err) {
      console.error('Error al desar l’actiu:', err);
      const msg = extractErrorMessage(err, 'Error en desar l’actiu');
      showNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Clic en canviar estat (Actiu / Inactiu)
  const handleRequestToggle = (item) => {
    if (item.actiu) {
      setToggleTarget(item);
      setOpenToggleDialog(true);
    } else {
      executeToggle(item);
    }
  };

  // Execució del toggle actiu
  const executeToggle = async (target) => {
    const item = target || toggleTarget;
    if (!item) return;
    setToggling(true);
    try {
      if (item.actiu) {
        // Desactivació: intenta PATCH /toggle-actiu, si falla per CORS preflight o 405, fallback a DELETE /{id}
        try {
          await axios.patch(
            `${config.apiBaseUrl}/v1/inversions/actius/${item.id}/toggle-actiu`,
            {},
            { headers: { Authorization: jwtToken } }
          );
        } catch (patchErr) {
          if (!patchErr.response || patchErr.response.status === 405) {
            await axios.delete(
              `${config.apiBaseUrl}/v1/inversions/actius/${item.id}`,
              { headers: { Authorization: jwtToken } }
            );
          } else {
            throw patchErr;
          }
        }
      } else {
        // Activació: intenta PATCH /toggle-actiu, si falla per CORS preflight o 405, fallback a PUT /{id}
        try {
          await axios.patch(
            `${config.apiBaseUrl}/v1/inversions/actius/${item.id}/toggle-actiu`,
            {},
            { headers: { Authorization: jwtToken } }
          );
        } catch (patchErr) {
          if (!patchErr.response || patchErr.response.status === 405) {
            await axios.put(
              `${config.apiBaseUrl}/v1/inversions/actius/${item.id}`,
              { ...item, actiu: true },
              { headers: { Authorization: jwtToken } }
            );
          } else {
            throw patchErr;
          }
        }
      }

      showNotification(
        `L’estat de l’actiu "${item.nom}" s’ha canviat a ${!item.actiu ? 'Actiu' : 'Inactiu'}`,
        'success'
      );
      setOpenToggleDialog(false);
      fetchActius();
    } catch (err) {
      console.error('Error al commutar estat de l’actiu:', err);
      const msg = extractErrorMessage(err, 'Error al canviar l’estat de l’actiu');
      showNotification(msg, 'error');
      setOpenToggleDialog(false);
    } finally {
      setToggling(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      {/* Filtres i Controls */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Cercar per nom, ISIN o tickers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={7} sm={3} md={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={onlyActive}
                  onChange={(e) => setOnlyActive(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {onlyActive ? 'Només actius' : 'Tots (inclou inactius)'}
                </Typography>
              }
            />
          </Grid>
          <Grid item xs={5} sm={3} md={3} sx={{ textAlign: 'right' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
              fullWidth={isMobile}
              sx={{ textTransform: 'none', fontWeight: 'bold' }}
            >
              Nou Actiu
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Estat de càrrega */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredActius.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No s’ha trobat cap actiu d’inversió per al compte <strong>{userapp}</strong> amb els filtres actuals.
        </Alert>
      ) : isMobile ? (
        /* VISTA MÒBIL: LLISTA DE TARGETES */
        <Stack spacing={1.5}>
          {filteredActius.map((a) => (
            <Card key={a.id} variant="outlined" sx={{ borderRadius: 2, boxShadow: 1 }}>
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {a.nom}
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    <Chip
                      label={a.tipusActiu || 'ACCIO'}
                      color={getTipusChipColor(a.tipusActiu)}
                      size="small"
                      sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}
                    />
                    <Chip
                      label={a.actiu ? 'Actiu' : 'Inactiu'}
                      color={a.actiu ? 'success' : 'default'}
                      size="small"
                      sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}
                    />
                  </Stack>
                </Box>

                <Grid container spacing={1} sx={{ mt: 0.5, fontSize: '0.85rem' }}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Ticker / ISIN:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {a.tickerIsin || '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Origen Preu:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {a.origenPreu || 'MANUAL'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Ticker Preu:
                    </Typography>
                    <Typography variant="body2">
                      {a.tickerPreu || '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Ticker Broker:
                    </Typography>
                    <Typography variant="body2">
                      {a.tickerBroker || '—'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
              <Divider />
              <CardActions sx={{ justifyContent: 'space-between', px: 2, py: 0.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={Boolean(a.actiu)}
                      onChange={() => handleRequestToggle(a)}
                      color="success"
                    />
                  }
                  label={
                    <Typography variant="caption" color="text.secondary">
                      {a.actiu ? 'Activat' : 'Desactivat'}
                    </Typography>
                  }
                />
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handleOpenEdit(a)}
                  sx={{ textTransform: 'none' }}
                >
                  Editar
                </Button>
              </CardActions>
            </Card>
          ))}
        </Stack>
      ) : (
        /* VISTA ESCRIPTORI: TAULA COMPLETA */
        <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: 'action.hover' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Nom de l’Actiu</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Ticker / ISIN</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Tipus</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Origen Preu</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Ticker Preu</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Ticker Broker</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Estat</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Accions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredActius.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{a.nom}</TableCell>
                  <TableCell>{a.tickerIsin || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={a.tipusActiu || 'ACCIO'}
                      color={getTipusChipColor(a.tipusActiu)}
                      size="small"
                      sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
                    />
                  </TableCell>
                  <TableCell>{a.origenPreu || 'MANUAL'}</TableCell>
                  <TableCell>{a.tickerPreu || '—'}</TableCell>
                  <TableCell>{a.tickerBroker || '—'}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={a.actiu ? 'Actiu' : 'Inactiu'}
                      color={a.actiu ? 'success' : 'default'}
                      size="small"
                      sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar dades de l’actiu">
                      <IconButton size="small" color="primary" onClick={() => handleOpenEdit(a)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={a.actiu ? 'Desactivar actiu' : 'Activar actiu'}>
                      <IconButton
                        size="small"
                        color={a.actiu ? 'default' : 'success'}
                        onClick={() => handleRequestToggle(a)}
                      >
                        <PowerSettingsNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* DIÀLEG: CREAR / EDITAR ACTIU */}
      <Dialog
        open={openModal}
        onClose={() => !saving && setOpenModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUpIcon color="primary" />
          {editingItem ? 'Editar Actiu d’Inversió' : 'Nou Actiu d’Inversió'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Nom de l’Actiu"
              fullWidth
              required
              value={formData.nom}
              onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
              placeholder="Ex: Vanguard S&P 500, Bitcoin, Physical Gold..."
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Ticker ISIN"
                  fullWidth
                  value={formData.tickerIsin}
                  onChange={(e) => setFormData({ ...formData, tickerIsin: e.target.value })}
                  placeholder="Ex: US0378331005, IE00BYX5MS15, bitcoin..."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipus d’Actiu</InputLabel>
                  <Select
                    value={formData.tipusActiu}
                    label="Tipus d’Actiu"
                    onChange={(e) => setFormData({ ...formData, tipusActiu: e.target.value })}
                  >
                    {availableTipusOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <FormControl fullWidth>
              <InputLabel>Origen de Preus de Cotització</InputLabel>
              <Select
                value={formData.origenPreu}
                label="Origen de Preus de Cotització"
                onChange={(e) => setFormData({ ...formData, origenPreu: e.target.value })}
              >
                {availableOrigenOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Ticker Preu (API Cotització)"
                  fullWidth
                  value={formData.tickerPreu}
                  onChange={(e) => setFormData({ ...formData, tickerPreu: e.target.value })}
                  placeholder="Ex: 0P0001CJH0, bitcoin, EGLN.L..."
                  helperText="Ticker emprat a Yahoo Finance o CoinGecko"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Ticker Broker"
                  fullWidth
                  value={formData.tickerBroker}
                  onChange={(e) => setFormData({ ...formData, tickerBroker: e.target.value })}
                  placeholder="Ex: EGLN.UK..."
                  helperText="Ticker emprat a la plataforma del broker (XTB)"
                />
              </Grid>
            </Grid>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.actiu}
                  onChange={(e) => setFormData({ ...formData, actiu: e.target.checked })}
                  color="success"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Actiu operatiu (Sincronització de preus activa)
                </Typography>
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenModal(false)} disabled={saving} color="inherit">
            Cancel·lar
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIÀLEG DE CONFIRMACIÓ DESACTIVACIÓ LÒGICA */}
      <Dialog
        open={openToggleDialog}
        onClose={() => !toggling && setOpenToggleDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Desactivar Actiu d’Inversió
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            L’actiu deixarà de sincronitzar cotitzacions automàtiques, però es conservaran intactes totes les transaccions de compra/venda registrades prèviament.
          </Alert>
          <Typography variant="body2">
            Vols confirmar la desactivació de <strong>"{toggleTarget?.nom}"</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenToggleDialog(false)} disabled={toggling} color="inherit">
            Cancel·lar
          </Button>
          <Button
            onClick={() => executeToggle()}
            variant="contained"
            color="warning"
            disabled={toggling}
          >
            {toggling ? <CircularProgress size={24} /> : 'Desactivar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActiusAdmin;
